import type { User } from "@types";
import { coachCsrAllowedRoutePaths } from "../../router/routeConfig";
import { permissionsForUser } from "./permissions";

/**
 * Candidate landing pages for a `coach_csr`, in preference order. The first one
 * the user can actually reach (per the permission-derived allowlist) is used.
 *
 * `/user-settings` is a permission-independent baseline always in the allowlist,
 * so this list always resolves to a reachable route — a coach_csr is never sent
 * to a page the route guard rejects. `/coach` itself is excluded (its index just
 * redirects coach_csr back to an allowed child — that's `CoachIndex`, which uses
 * this very helper).
 */
const COACH_CSR_LANDING_PREFERENCE: readonly string[] = [
  "/coach/conversations",
  "/coach/agent-performance",
  "/user-settings",
];

/**
 * The safe landing for a `coach_csr`: the first reachable page in
 * `COACH_CSR_LANDING_PREFERENCE`, derived from the SAME permission-allowlist as
 * the route guard — so it can never be a route the guard forbids (no bounce/loop).
 *
 * Shared by post-login redirect (`getPostLoginPath`) and the `/coach` index
 * redirect (`CoachIndex`) so the landing logic lives in exactly one place.
 */
export const coachCsrLanding = (user?: User): string => {
  const allowed = coachCsrAllowedRoutePaths(permissionsForUser(user));
  return (
    COACH_CSR_LANDING_PREFERENCE.find((path) => allowed.has(path)) ??
    "/user-settings"
  );
};
